import {
  Select,
  MultiSelect,
  TextInput,
  Alert,
  Text,
  Stack,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import type { ReactNode } from "react";
import type { LookupOption } from "@livingsyncs/types";
import type { LookupValue } from "./lookupFieldValue";

export interface EntityLookupFieldProps {
  label: ReactNode;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  /** Shown in the disabled "resolved at fill time" state and default placeholders, e.g. "resident's active medications". */
  entityLabel: string;
  /**
   * Real, backend-sourced choices this field can resolve against. `undefined`
   * means no context is available on this surface (form-builder preview, a
   * staff/facility form with no resident in scope) — per US-133 AC-6, this
   * must NEVER fall back to an editable free-text box that looks like a
   * working lookup; it renders a disabled "resolved at fill time" state
   * instead. An empty array (`[]`) means context IS available but there's
   * nothing to pick — a normal searchable picker with no results, not the
   * disabled state.
   */
  options: LookupOption[] | undefined;
  /** Choose-many (e.g. medication, allergy) vs single-entity lookup. */
  multiple?: boolean;
  /**
   * Code-review fix: a REQUIRED field with no options available must never
   * be a permanent dead end — that made the form unsubmittable on any
   * surface that doesn't (yet) wire real lookupOptions (e.g. an unlocked
   * staff_lookup/facility_lookup field on StaffFormsSection/
   * FacilityFormsSection/SupervisedSessionPage before those were wired).
   * When true and options is undefined, render an editable manual-entry
   * fallback instead of the disabled badge — clearly marked as unresolved,
   * never pretending to be a validated lookup, but at least fillable.
   */
  required?: boolean;
  value: LookupValue;
  onChange: (value: string | string[] | null) => void;
}

/**
 * US-133 AC-2/AC-4: the one shared, real entity-picker used by every lookup
 * field type (resident/staff/facility/medication/allergy/emergency-contact/
 * insurance/care-plan/external-provider) — always sourced from real backend
 * data, never a static/AI-invented option list, and never a free-text box
 * silently standing in for a lookup.
 */
export function EntityLookupField({
  label,
  description,
  placeholder,
  disabled,
  error,
  entityLabel,
  options,
  multiple,
  required,
  value,
  onChange,
}: EntityLookupFieldProps) {
  if (options === undefined) {
    if (required) {
      return (
        <TextInput
          label={label}
          description={
            description ??
            `No ${entityLabel} directory is available on this screen — enter manually and verify before submitting.`
          }
          placeholder={placeholder ?? "Manual entry — unresolved"}
          disabled={disabled}
          error={error}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      );
    }
    return (
      <Stack gap={4}>
        {typeof label === "string" ? (
          <Text size="sm" fw={500}>
            {label}
          </Text>
        ) : (
          label
        )}
        {description && (
          <Text size="xs" c="dimmed">
            {description}
          </Text>
        )}
        <Alert
          icon={<IconInfoCircle size={14} />}
          color="gray"
          variant="light"
          p="xs"
        >
          <Text size="sm" c="dimmed">
            Resolved from {entityLabel} at fill time
          </Text>
        </Alert>
      </Stack>
    );
  }

  if (multiple) {
    return (
      <MultiSelect
        label={label}
        description={description}
        placeholder={placeholder ?? `Select ${entityLabel}...`}
        disabled={disabled}
        error={error}
        data={options}
        value={Array.isArray(value) ? value : value ? [value] : []}
        onChange={onChange}
        searchable
        clearable
      />
    );
  }

  return (
    <Select
      label={label}
      description={description}
      placeholder={placeholder ?? `Search ${entityLabel}...`}
      disabled={disabled}
      error={error}
      data={options}
      value={typeof value === "string" ? value : null}
      onChange={onChange}
      searchable
      clearable
    />
  );
}
